'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { useCadrinoCopy } from '@/lib/cadrino-landing/useCadrinoCopy';
import { useLandingLanguage } from '@/hooks/useLandingLanguage';
import { useClientMounted } from '@/hooks/useClientMounted';
import { Container, Section, SectionHeader, MotionBlock, PrimaryButton } from './shared';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DashboardChrome } from './product/ProductMocks';
import { ErvAttendanceSimulationSkeleton } from './simulation/ErvAttendanceSimulationSkeleton';
import { getAssignmentEmployees, type SimAssignEmployee } from '@/lib/cadrino-landing/assignment-simulation-data';
import { cn } from '@/lib/utils';
const ErvAssignmentSimulation = dynamic(() => import('./simulation/ErvAssignmentSimulation').then(m => m.ErvAssignmentSimulation), {
  ssr: false,
  loading: () => <div className="min-h-[360px] animate-pulse rounded-xl bg-gray-100" />
});
const ErvUsersSimulation = dynamic(() => import('./simulation/ErvUsersSimulation').then(m => m.ErvUsersSimulation), {
  ssr: false,
  loading: () => <div className="min-h-[280px] animate-pulse rounded-xl bg-gray-100" />
});
const ErvAttendanceSimulation = dynamic(() => import('./simulation/ErvAttendanceSimulation').then(m => m.ErvAttendanceSimulation), {
  ssr: false,
  loading: () => <ErvAttendanceSimulationSkeleton />
});
type OpsTab = 'users' | 'matrix' | 'outcome';
export function ProductOperationsShowcase() {
  const $ = _c(39);
  const o = useCadrinoCopy().productDemo.operations;
  const {
    language
  } = useLandingLanguage();
  const lang = language === "en" ? "en" : "sr";
  const [tab, setTab] = useState("users");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [users, setUsers] = useState(t0);
  const mounted = useClientMounted();
  let t1;
  let t2;
  if ($[1] !== lang) {
    t1 = () => {
      setUsers(getAssignmentEmployees(lang));
    };
    t2 = [lang];
    $[1] = lang;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== lang) {
    t3 = lang === "en" ? {
      tabUsers: "1. Users",
      tabMatrix: "2. Assignment matrix",
      tabOutcome: "3. Attendance",
      usersWindow: "Cadrino ERV \xB7 users (demo)",
      matrixWindow: "Cadrino ERV \xB7 assignment matrix (demo)",
      nextToMatrix: "2. Assignment matrix",
      nextToOutcome: "3. Attendance"
    } : {
      tabUsers: "1. Korisnici",
      tabMatrix: "2. Dodela korisnika",
      tabOutcome: "3. Evidencija",
      usersWindow: "Cadrino ERV \xB7 korisnici (demo)",
      matrixWindow: "Cadrino ERV \xB7 dodela korisnika (demo)",
      nextToMatrix: "2. Dodela korisnika",
      nextToOutcome: "3. Evidencija"
    };
    $[4] = lang;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const uiText = t3;
  let t4;
  if ($[6] !== uiText.tabUsers) {
    t4 = {
      value: "users",
      label: uiText.tabUsers
    };
    $[6] = uiText.tabUsers;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== uiText.tabMatrix) {
    t5 = {
      value: "matrix",
      label: uiText.tabMatrix
    };
    $[8] = uiText.tabMatrix;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== uiText.tabOutcome) {
    t6 = {
      value: "outcome",
      label: uiText.tabOutcome
    };
    $[10] = uiText.tabOutcome;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = [t4, t5, t6];
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const tabs = t7;
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (firstName, lastName, cardNumber) => {
      const fullName = `${firstName} ${lastName}`.trim();
      setUsers(prev => [...prev, {
        id: `emp-new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        firstName,
        lastName,
        cardNumber,
        name: fullName
      }]);
    };
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleAddUser = t8;
  let t9;
  if ($[17] !== o.badge) {
    t9 = <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-cadrino-accent">{o.badge}</p>;
    $[17] = o.badge;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== o.subtitle || $[20] !== o.title) {
    t10 = <SectionHeader title={o.title} subtitle={o.subtitle} />;
    $[19] = o.subtitle;
    $[20] = o.title;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = <MotionBlock>{t9}{t10}</MotionBlock>;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== mounted || $[26] !== o.windowTitles || $[27] !== tab || $[28] !== tabs || $[29] !== uiText.matrixWindow || $[30] !== uiText.usersWindow || $[31] !== users) {
    t12 = <MotionBlock className="mt-8">{!mounted ? <div className="w-full"><div className="mb-6 flex h-auto w-full justify-start gap-1 rounded-xl border border-white/[0.1] bg-cadrino-surface p-1.5">{tabs.map(_temp)}</div><DashboardChrome title={uiText.usersWindow}><div className="-m-4 min-h-[280px] animate-pulse rounded-xl bg-gray-100" /></DashboardChrome></div> : <Tabs value={tab} onValueChange={v => setTab(v as OpsTab)} className="w-full"><TabsList className="mb-6 h-auto w-full justify-start gap-1 rounded-xl border border-white/[0.1] bg-cadrino-surface p-1.5 text-slate-400">{tabs.map(_temp2)}</TabsList><TabsContent value="users" className="mt-0" forceMount={true}><div className={cn(tab !== "users" && "hidden")}><DashboardChrome title={uiText.usersWindow}><div className="-m-4"><ErvUsersSimulation users={users} onAddUser={handleAddUser} /></div></DashboardChrome></div></TabsContent><TabsContent value="matrix" className="mt-0" forceMount={true}><div className={cn(tab !== "matrix" && "hidden")}><DashboardChrome title={uiText.matrixWindow}><div className="-m-4"><ErvAssignmentSimulation active={tab === "matrix"} users={users} /></div></DashboardChrome></div></TabsContent><TabsContent value="outcome" className="mt-0" forceMount={true}><div className={cn(tab !== "outcome" && "hidden")}><DashboardChrome title={o.windowTitles.outcome}><div className="-m-4"><ErvAttendanceSimulation active={tab === "outcome"} /></div></DashboardChrome></div></TabsContent></Tabs>}</MotionBlock>;
    $[25] = mounted;
    $[26] = o.windowTitles;
    $[27] = tab;
    $[28] = tabs;
    $[29] = uiText.matrixWindow;
    $[30] = uiText.usersWindow;
    $[31] = users;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== o.ctaDemo) {
    t13 = <MotionBlock className="mt-8"><PrimaryButton href="#contact">{o.ctaDemo}</PrimaryButton></MotionBlock>;
    $[33] = o.ctaDemo;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t11 || $[36] !== t12 || $[37] !== t13) {
    t14 = <Section id="demo" className="border-t border-slate-200/80 bg-slate-50/60"><Container>{t11}{t12}{t13}</Container></Section>;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  return t14;
}
function _temp2(t0) {
  const {
    value: value_0,
    label: label_0
  } = t0;
  return <TabsTrigger key={value_0} value={value_0} className={cn("flex-1 rounded-lg border border-transparent px-3 py-2.5 text-sm font-medium shadow-none transition-all duration-200 sm:px-4", "text-slate-400 hover:bg-white/[0.04] hover:text-slate-200", "data-[state=active]:border-white/[0.1] data-[state=active]:bg-white/[0.1]", "data-[state=active]:text-slate-50 data-[state=active]:shadow-none", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cadrino-primary/50")}>{label_0}</TabsTrigger>;
}
function _temp(t0) {
  const {
    value,
    label
  } = t0;
  return <div key={value} className={cn("flex-1 rounded-lg px-3 py-2.5 text-center text-sm font-medium sm:px-4", value === "users" ? "border border-white/[0.1] bg-white/[0.1] text-slate-50" : "text-slate-400")}>{label}</div>;
}